/**
 * VIDEO COMPRESSION SERVICE
 * ==========================
 * Compresses videos before upload using FFmpeg WASM (runs entirely in the browser).
 *
 * WHY FFMPEG WASM:
 *  - DSLR / GoPro videos are typically 25–120 Mbps (huge files).
 *  - Re-encoding to H.264 reduces file size significantly.
 *  - Runs on the promoter's machine — zero server CPU cost.
 *
 * COMPRESSION SETTINGS:
 *  - Codec:   H.264 (libx264) — universally compatible
 *  - Quality: CRF 23 — x264 default, visually lossless for most content.
 *  - Preset:  ultrafast — only WASM-safe preset. superfast/veryfast crash.
 *  - Bitrate: -maxrate 8M -bufsize 16M — caps output so ultrafast can't
 *             waste bits. Keeps file size reasonable.
 *  - Resolution: PRESERVED — no scaling. 1080p stays 1080p, 4K stays 4K.
 *  - FPS:     PRESERVED — original framerate kept.
 *  - Audio:   -c:a copy — no re-encode, instant pass-through
 *  - Output:  .mp4 with +faststart (web-optimised streaming)
 *
 * TRIGGER THRESHOLD:
 *  - TARGET_MBPS = 20 — only videos above 20 Mbps average bitrate get compressed.
 *  - Lower bitrate clips keep original file to avoid unnecessary re-encode.
 *
 * NOTE: -tune zerolatency and presets above ultrafast crash with WASM
 *       "function signature mismatch" RuntimeError.
 */

export const TARGET_MBPS = 20;
// Set to true to skip compression and upload originals directly
export const COMPRESSION_DISABLED = false;

/** Human-readable label for the FFmpeg encoder that was used. */
export function encoderLabel(enc) {
  if (enc === "h264_nvenc") return "GPU + CPU: NVIDIA NVENC";
  if (enc === "h264_qsv")  return "GPU + CPU: Intel Quick Sync";
  if (enc === "h264_amf")  return "GPU + CPU: AMD AMF";
  if (enc === "libx264")   return "CPU: libx264";
  return enc || "";
}

/* ── WASM Worker singleton (DISABLED — all compression now goes through PSA Studio) ──
 * The WASM path is kept here for reference but is no longer used.
 * If Studio is not running, uploads requiring compression are blocked.
 * ─────────────────────────────────────────────────────────────────────────────────────────── */

// let _worker = null;
// let _msgId = 0;
// const _pending = {};

// function getWorker() {
//   if (_worker) return _worker;
//   _worker = new Worker(`${window.location.origin}/ffmpegWorker.js`);
//   _worker.onmessage = ({ data }) => {
//     const { type, id, payload } = data;
//     const p = _pending[id];
//     if (!p) return;
//     if (type === "progress") {
//       p.onProgress?.(payload.ratio);
//     } else if (type === "done" || type === "loaded") {
//       delete _pending[id];
//       p.resolve(payload);
//     } else if (type === "error") {
//       delete _pending[id];
//       p.reject(new Error(payload?.message || "FFmpeg worker error"));
//     }
//   };
//   _worker.onerror = (err) => {
//     console.error("[FFmpeg Worker] Worker crashed:", err);
//     Object.values(_pending).forEach((p) => p.reject(err));
//     for (const k in _pending) delete _pending[k];
//     _worker = null;
//   };
//   return _worker;
// }

/** No-op: WASM pre-warm disabled. Studio is used exclusively for compression. */
export const loadFFmpeg = () => Promise.resolve();

export const getVideoMeta = (file) =>
  new Promise((resolve, reject) => {
    if (COMPRESSION_DISABLED) {
      resolve({ needsCompression: false, duration: 0, width: 0, height: 0, bitrateMbps: 0 });
      return;
    }
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";

    // Timeout: if the browser can't parse metadata within 15s (common with
    // large files where moov atom is at the end), assume compression is needed.
    const META_TIMEOUT_MS = 15000;
    let settled = false;
    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(url);
      console.warn(`[VideoMeta] "${file.name}" metadata timeout after ${META_TIMEOUT_MS / 1000}s — assuming compression needed.`);
      resolve({ needsCompression: true, duration: 0, width: 0, height: 0, bitrateMbps: 0 });
    }, META_TIMEOUT_MS);

    video.onloadedmetadata = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      URL.revokeObjectURL(url);
      const dur = video.duration;

      if (!dur || !isFinite(dur) || dur <= 0) {
        console.warn(`[VideoMeta] "${file.name}" could not read duration, skipping compression.`);
        resolve({ needsCompression: false, duration: 0, width: video.videoWidth, height: video.videoHeight, bitrateMbps: 0 });
        return;
      }

      const bitrateMbps = +((file.size * 8) / dur / 1_000_000).toFixed(2);
      const needsCompression = bitrateMbps > TARGET_MBPS;
      console.log(
        `[VideoMeta] "${file.name}" | ${(file.size / 1e6).toFixed(2)} MB | ` +
        `${dur.toFixed(2)}s | ${video.videoWidth}x${video.videoHeight} | ` +
        `${bitrateMbps} Mbps | needsCompression: ${needsCompression}`
      );
      resolve({ bitrateMbps, duration: dur, width: video.videoWidth, height: video.videoHeight, needsCompression });
    };

    video.onerror = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      URL.revokeObjectURL(url);
      reject(new Error(`Cannot read metadata for "${file.name}"`));
    };

    video.src = url;
  });

// ── Desktop companion app ────────────────────────────────────────────────────
// A locally-installed Electron app exposes a small HTTP server on this port.
// It runs native FFmpeg (no WASM limits) — handles any file size, better presets.
//
// API contract the companion must implement:
//   GET  /ping                   → 200 { ok: true }
//   POST /compress               → multipart: file, width, height, quality, threads, hwaccel  → 200 { jobId }
//                                    threads : number  — logical CPU cores (navigator.hardwareConcurrency)
//                                    hwaccel : "true"  — try GPU encoder chain before libx264
//                                              GPU chain: h264_nvenc (NVIDIA) → h264_amf (AMD) → h264_qsv (Intel) → libx264 (CPU fallback)
//   GET  /progress/:jobId        → SSE stream: { ratio: 0.0–1.0 }, ends with { ratio: 1.0, done: true }
//   GET  /result/:jobId          → binary .mp4 download
//   DELETE /result/:jobId        → delete temp input + output files for this job (called after download)
// ─────────────────────────────────────────────────────────────────────────────
// Use direct localhost for heavy compression transfer paths.
// Dev proxy can fail with very large multipart bodies (HTTP 500 via /companion-api).
const COMPANION_DIRECT = "http://localhost:7891";
// const MAX_WASM_MB = 800; // Unused — WASM path is disabled
let _companionStatus = null; // null=unchecked, true=available, false=unavailable
let _companionVersion = null; // version string reported by the companion's /ping, e.g. "1.0.6"
let _companionLastChecked = 0; // timestamp of last successful probe
const COMPANION_STATUS_TTL_MS = 30_000; // re-ping every 30 seconds

export const INSUFFICIENT_COMPANION_DISK_SPACE = "INSUFFICIENT_COMPANION_DISK_SPACE";
export const COMPANION_DISK_SPACE_CHECK_UNAVAILABLE = "COMPANION_DISK_SPACE_CHECK_UNAVAILABLE";

const MB = 1024 * 1024;
const GB = 1024 * MB;

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 MB";
  if (bytes >= GB) return `${(bytes / GB).toFixed(2)} GB`;
  return `${Math.ceil(bytes / MB)} MB`;
};

// Compression on Studio generally needs input + output + a safety margin on C:.
const estimateRequiredFreeBytes = (fileSizeBytes) => {
  const margin = Math.max(512 * MB, Math.round(fileSizeBytes * 0.15));
  return (fileSizeBytes * 2) + margin;
};

const parseFreeSpaceBytes = (data) => {
  const candidates = [
    data?.freeBytes,
    data?.freeSpaceBytes,
    data?.availableBytes,
    data?.result?.freeBytes,
    data?.result?.freeSpaceBytes,
    data?.result?.availableBytes,
  ];
  const found = candidates.find((v) => Number.isFinite(Number(v)) && Number(v) >= 0);
  return found == null ? null : Number(found);
};

export const checkCompanionDiskSpace = async ({ fileSizeBytes, drive = "C" } = {}) => {
  const requiredBytes = estimateRequiredFreeBytes(fileSizeBytes || 0);

  let res;
  try {
    res = await fetch(
      `${COMPANION_DIRECT}/disk-space?drive=${encodeURIComponent(drive)}&requiredBytes=${requiredBytes}`,
      { signal: AbortSignal.timeout(2500) }
    );
  } catch (err) {
    const wrapped = new Error("Could not verify free space on C: drive from PSA Studio.");
    wrapped.code = COMPANION_DISK_SPACE_CHECK_UNAVAILABLE;
    wrapped.details = {
      drive,
      requiredBytes,
      requiredText: formatBytes(requiredBytes),
      reason: "network",
      rawMessage: err?.message || "Request failed",
    };
    throw wrapped;
  }

  if (!res.ok) {
    const wrapped = new Error(
      res.status === 404
        ? "Your PSA Studio is outdated and cannot verify free space. Please update Studio and retry."
        : "PSA Studio could not verify free space on C: drive. Please retry."
    );
    wrapped.code = COMPANION_DISK_SPACE_CHECK_UNAVAILABLE;
    wrapped.details = {
      drive,
      requiredBytes,
      requiredText: formatBytes(requiredBytes),
      status: res.status,
    };
    throw wrapped;
  }

  let data;
  try {
    data = await res.json();
  } catch {
    const wrapped = new Error("PSA Studio returned an invalid free-space response.");
    wrapped.code = COMPANION_DISK_SPACE_CHECK_UNAVAILABLE;
    wrapped.details = {
      drive,
      requiredBytes,
      requiredText: formatBytes(requiredBytes),
      reason: "invalid-json",
    };
    throw wrapped;
  }
  const freeBytes = parseFreeSpaceBytes(data);
  if (!Number.isFinite(freeBytes)) {
    const wrapped = new Error("PSA Studio free-space response is incomplete.");
    wrapped.code = COMPANION_DISK_SPACE_CHECK_UNAVAILABLE;
    wrapped.details = {
      drive,
      requiredBytes,
      requiredText: formatBytes(requiredBytes),
      reason: "missing-free-bytes",
    };
    throw wrapped;
  }

  const hasEnoughSpace = freeBytes >= requiredBytes;
  return {
    hasEnoughSpace,
    drive,
    freeBytes,
    requiredBytes,
    freeText: formatBytes(freeBytes),
    requiredText: formatBytes(requiredBytes),
  };
};

/** Force a fresh probe on next checkCompanion() call — used by the install page recheck button */
export const _resetCompanionStatus = () => { _companionStatus = null; _companionVersion = null; _companionLastChecked = 0; };

/** Returns the version string last reported by the companion's /ping (e.g. "1.0.6"), or null if not yet checked / not running. */
export const getCompanionVersion = () => _companionVersion;

/**
 * Probe the local companion app. Re-checks every 30 s so Studio restarts are detected quickly.
 * Always pings the DIRECT URL (localhost:7891) — small JSON, no COEP issue,
 * and needs to verify the actual companion process is running.
 */
export const checkCompanion = async () => {
  const now = Date.now();
  if (_companionStatus !== null && (now - _companionLastChecked) < COMPANION_STATUS_TTL_MS) {
    return _companionStatus;
  }
  try {
    const res = await fetch(`${COMPANION_DIRECT}/ping`, { signal: AbortSignal.timeout(1500) });
    _companionStatus = res.ok;
    if (res.ok) {
      try {
        const data = await res.json();
        _companionVersion = data?.version || null;
      } catch { _companionVersion = null; }
    } else {
      _companionVersion = null;
    }
  } catch {
    _companionStatus = false;
    _companionVersion = null;
  }
  _companionLastChecked = Date.now();
  console.log(`[Companion] ${_companionStatus ? "✅ Available — using native FFmpeg" : "❌ Not running — using WASM"}`);
  return _companionStatus;
};

/**
 * Compress via companion app (native FFmpeg — any file size, no ultrafast restriction).
 * Three-step: submit job → stream SSE progress → download result.
 */
const compressViaCompanion = async (
  file,
  { onProgress, onEncoder, width = 0, height = 0, quality = "original", watermark = null } = {}
) => {
  const sizeMB = (file.size / 1e6).toFixed(2);
  console.group(`[Companion] Compressing "${file.name}"`);
  console.log(`  Size:       ${sizeMB} MB`);
  console.log(`  Resolution: ${width || "?"}x${height || "?"}`);
  console.log(`  Quality:    ${quality}`);
  if (watermark?.WatermarkImageUrl) {
    console.log("  Mode:       Compressing + applying watermark");
    console.log(
      `  Watermark:  enabled | id=${watermark.Id || "n/a"} | pos=${watermark.Position || "Center"} | opacity=${watermark.Opacity ?? "n/a"} | size=${watermark.SizePercent ?? "n/a"}`
    );
  } else {
    console.log("  Watermark:  disabled");
  }
  const t0 = performance.now();

  // Step 1: submit job — use XHR so we can report upload-to-companion progress.
  // For large files (e.g. 2 GB) the loopback transfer can take a few seconds.
  // We report 0–30% for the send phase, 30–100% for the encode phase.
  const form = new FormData();
  form.append("file", file);
  form.append("width", String(width));
  form.append("height", String(height));
  form.append("quality", quality);
  // Max CPU cores + GPU acceleration hint for the companion's native FFmpeg
  form.append("threads", String(navigator.hardwareConcurrency || 0)); // 0 = FFmpeg auto
  form.append("hwaccel", "true"); // companion should try h264_nvenc → h264_amf → h264_qsv → libx264
  if (watermark?.WatermarkImageUrl) {
    form.append("watermarkEnabled", "true");
    if (watermark.Id) form.append("watermarkId", String(watermark.Id));
    form.append("watermarkImageUrl", String(watermark.WatermarkImageUrl));
    if (watermark.Position) form.append("watermarkPosition", String(watermark.Position));
    if (watermark.Opacity != null) form.append("watermarkOpacity", String(watermark.Opacity));
    if (watermark.SizePercent != null) form.append("watermarkSizePercent", String(watermark.SizePercent));
    if (watermark.UpdatedAt) form.append("watermarkUpdatedAt", String(watermark.UpdatedAt));
  }

  const jobId = await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${COMPANION_DIRECT}/compress`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        // Map upload progress to 0–0.30 of the total progress bar
        onProgress(Math.max(0, Math.min(0.30, (e.loaded / e.total) * 0.30)));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText).jobId); }
        catch { reject(new Error("Companion returned invalid JSON")); }
      } else {
        reject(new Error(`Companion rejected: HTTP ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error("Companion connection failed during upload"));
    xhr.send(form);
  });

  // Step 2: stream encode progress via SSE (maps 30–100% of the progress bar)
  const { inputSize: sseInputSize, outputSize: sseOutputSize } = await new Promise((resolve, reject) => {
    let accelerationLogged = false;
    let _inputSize = 0;
    let _outputSize = 0;
    const es = new EventSource(`${COMPANION_DIRECT}/progress/${jobId}`);
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (!accelerationLogged && (data.acceleration || data.encoder)) {
          accelerationLogged = true;
          console.log(`  Acceleration: ${data.acceleration}${data.encoder ? ` (${data.encoder})` : ""}`);
          if (data.encoder) onEncoder?.(data.encoder);
        }
        if (data.ratio !== undefined) {
          // Encode phase occupies 0.30–1.0 of the bar
          onProgress(0.30 + Math.max(0, Math.min(0.70, data.ratio * 0.70)));
        }
        if (data.inputSize)  _inputSize  = data.inputSize;
        if (data.outputSize) _outputSize = data.outputSize;
        if (data.done)  { es.close(); resolve({ inputSize: _inputSize, outputSize: _outputSize }); }
        if (data.error) { es.close(); reject(new Error(data.error)); }
      } catch { /* ignore malformed events */ }
    };
    es.onerror = () => { es.close(); reject(new Error("Companion SSE connection lost")); };
  });

  // Step 3: download compressed result using fetch() + ReadableStream.
  // Streaming avoids responseType=blob failures for large files.
  const blob = await (async () => {
    const res = await fetch(`${COMPANION_DIRECT}/result/${jobId}`);
    if (!res.ok) throw new Error(`Companion result fetch failed: HTTP ${res.status}`);

    // Fast path: if ReadableStream is available, read in chunks (handles any size)
    if (res.body && res.body.getReader) {
      const reader = res.body.getReader();
      const chunks = [];
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
      }
      return new Blob(chunks, { type: "video/mp4" });
    }

    // Fallback: use .blob() directly (works for smaller files)
    return res.blob();
  })();

  fetch(`${COMPANION_DIRECT}/result/${jobId}`, { method: "DELETE" }).catch(() => {});

  const outputName = file.name.replace(/\.[^.]+$/, ".mp4");
  const outFile = new File([blob], outputName, { type: "video/mp4" });

  // Use server-reported sizes if available, fall back to JS File.size
  const reportedInputMB  = sseInputSize  > 0 ? (sseInputSize  / 1e6).toFixed(1) : (file.size    / 1e6).toFixed(1);
  const reportedOutputMB = sseOutputSize > 0 ? (sseOutputSize / 1e6).toFixed(1) : (outFile.size / 1e6).toFixed(1);
  const reportedReduction = sseInputSize > 0 && sseOutputSize > 0
    ? Math.round((1 - sseOutputSize / sseInputSize) * 100)
    : Math.round((1 - outFile.size / file.size) * 100);

  // Safety guard: never replace source with a larger "compressed" file.
  if (outFile.size >= file.size && !watermark?.WatermarkImageUrl) {
    const totalSec = ((performance.now() - t0) / 1000).toFixed(1);
    const diffPct = Math.round((outFile.size / file.size - 1) * 100);
    console.warn(`  ⚠️ No gain in ${totalSec}s — output is ${diffPct}% larger (${reportedOutputMB} MB). Using original file.`);
    console.groupEnd();
    return file;
  }

  const totalSec = ((performance.now() - t0) / 1000).toFixed(1);
  console.log(
    `%c  ✅ Done in ${totalSec}s — ${reportedInputMB} MB → ${reportedOutputMB} MB (${reportedReduction}% smaller)`,
    "color: #22c55e; font-weight: bold;"
  );
  console.groupEnd();
  return outFile;
};

/**
 * Compress image via companion app (native FFmpeg — any file size, no size limit).
 * Same flow as video: submit job → stream SSE progress → download result.
 */
const compressImageViaCompanion = async (
  file,
  { onProgress, onEncoder, watermark = null } = {}
) => {
  const sizeMB = (file.size / 1e6).toFixed(2);
  console.group(`[Companion] Compressing image "${file.name}"`);
  console.log(`  Size:       ${sizeMB} MB`);
  if (watermark?.WatermarkImageUrl) {
    console.log("  Mode:       Compressing + applying watermark");
    console.log(
      `  Watermark:  enabled | id=${watermark.Id || "n/a"} | pos=${watermark.Position || "Center"} | opacity=${watermark.Opacity ?? "n/a"} | size=${watermark.SizePercent ?? "n/a"}`
    );
  } else {
    console.log("  Watermark:  disabled");
  }
  const t0 = performance.now();

  // Step 1: submit job with image file
  const form = new FormData();
  form.append("file", file);
  form.append("isImage", "true"); // Signal to companion this is an image
  form.append("threads", String(navigator.hardwareConcurrency || 0));
  form.append("hwaccel", "false"); // Images don't need GPU — CPU is fine
  if (watermark?.WatermarkImageUrl) {
    form.append("watermarkEnabled", "true");
    if (watermark.Id) form.append("watermarkId", String(watermark.Id));
    form.append("watermarkImageUrl", String(watermark.WatermarkImageUrl));
    if (watermark.Position) form.append("watermarkPosition", String(watermark.Position));
    if (watermark.Opacity != null) form.append("watermarkOpacity", String(watermark.Opacity));
    if (watermark.SizePercent != null) form.append("watermarkSizePercent", String(watermark.SizePercent));
    if (watermark.UpdatedAt) form.append("watermarkUpdatedAt", String(watermark.UpdatedAt));
  }

  const jobId = await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${COMPANION_DIRECT}/compress`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress(Math.max(0, Math.min(0.30, (e.loaded / e.total) * 0.30)));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText).jobId); }
        catch { reject(new Error("Companion returned invalid JSON")); }
      } else {
        reject(new Error(`Companion rejected: HTTP ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error("Companion connection failed during upload"));
    xhr.send(form);
  });

  // Step 2: stream encode progress via SSE
  const { inputSize: sseInputSize, outputSize: sseOutputSize } = await new Promise((resolve, reject) => {
    let accelerationLogged = false;
    let _inputSize = 0;
    let _outputSize = 0;
    const es = new EventSource(`${COMPANION_DIRECT}/progress/${jobId}`);
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (!accelerationLogged && data.acceleration) {
          accelerationLogged = true;
          console.log(`  Acceleration: ${data.acceleration}`);
        }
        if (data.ratio !== undefined) {
          onProgress(0.30 + Math.max(0, Math.min(0.70, data.ratio * 0.70)));
        }
        if (data.inputSize)  _inputSize  = data.inputSize;
        if (data.outputSize) _outputSize = data.outputSize;
        if (data.done)  { es.close(); resolve({ inputSize: _inputSize, outputSize: _outputSize }); }
        if (data.error) { es.close(); reject(new Error(data.error)); }
      } catch { /* ignore malformed events */ }
    };
    es.onerror = () => { es.close(); reject(new Error("Companion SSE connection lost")); };
  });

  // Step 3: download compressed result
  const blob = await (async () => {
    const res = await fetch(`${COMPANION_DIRECT}/result/${jobId}`);
    if (!res.ok) throw new Error(`Companion result fetch failed: HTTP ${res.status}`);
    if (res.body && res.body.getReader) {
      const reader = res.body.getReader();
      const chunks = [];
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
      }
      return new Blob(chunks, { type: "image/jpeg" });
    }
    return res.blob();
  })();

  fetch(`${COMPANION_DIRECT}/result/${jobId}`, { method: "DELETE" }).catch(() => {});

  const outputName = file.name.replace(/\.[^.]+$/, ".jpg");
  const outFile = new File([blob], outputName, { type: "image/jpeg" });

  // Safety: never replace with larger file
  if (outFile.size >= file.size && !watermark?.WatermarkImageUrl) {
    const totalSec = ((performance.now() - t0) / 1000).toFixed(1);
    const diffPct = Math.round((outFile.size / file.size - 1) * 100);
    console.warn(`  ⚠️ No gain in ${totalSec}s — output is ${diffPct}% larger. Using original.`);
    console.groupEnd();
    return file;
  }

  const totalSec = ((performance.now() - t0) / 1000).toFixed(1);
  const reportedInputMB  = sseInputSize  > 0 ? (sseInputSize  / 1e6).toFixed(1) : (file.size    / 1e6).toFixed(1);
  const reportedOutputMB = sseOutputSize > 0 ? (sseOutputSize / 1e6).toFixed(1) : (outFile.size / 1e6).toFixed(1);
  const reportedReduction = sseInputSize > 0 && sseOutputSize > 0
    ? Math.round((1 - sseOutputSize / sseInputSize) * 100)
    : Math.round((1 - outFile.size / file.size) * 100);

  console.log(
    `%c  ✅ Done in ${totalSec}s — ${reportedInputMB} MB → ${reportedOutputMB} MB (${reportedReduction}% smaller)`,
    "color: #22c55e; font-weight: bold;"
  );
  console.groupEnd();
  return outFile;
};

export const compressVideo = async (
  file,
  { onProgress, onEncoder, width = 0, height = 0, quality = "original", watermark = null } = {}
) => {
  // -- Studio-only routing ---------------------------------------------------
  // WASM fallback is disabled. All compression runs through PSA Studio.
  // If Studio is not running this function throws -- callers must handle that.
  const companion = await checkCompanion();
  if (!companion) {
    throw new Error("PSA Studio is not running. Please install and launch the Studio app to upload videos.");
  }

  const disk = await checkCompanionDiskSpace({ fileSizeBytes: file.size, drive: "C" });
  if (!disk.hasEnoughSpace) {
    const err = new Error(
      `Not enough free space on C: drive. Required ${disk.requiredText}, available ${disk.freeText}.`
    );
    err.code = INSUFFICIENT_COMPANION_DISK_SPACE;
    err.details = disk;
    throw err;
  }

  const isWatermarkJob = !!watermark?.WatermarkImageUrl;

  const out = await compressViaCompanion(file, { onProgress, onEncoder, width, height, quality, watermark });

  // ── Watermark: single pass, always upload — watermark re-encode forces higher bitrate ──
  // Size may legitimately increase (overlay requires full re-encode). Never retry.
  if (isWatermarkJob) {
    if (out.size > file.size) {
      console.warn(
        `[Companion] Watermarked "${file.name}" is larger than original ` +
        `(${(file.size / 1e6).toFixed(1)} MB → ${(out.size / 1e6).toFixed(1)} MB) — uploading anyway.`
      );
    }
    return out;
  }

  // ── No watermark: strict no-size-regression (use original if output is larger) ──
  if (out.size >= file.size) {
    console.warn(
      `[Companion] Compression of "${file.name}" produced larger output ` +
      `(${(out.size / 1e6).toFixed(1)} MB > ${(file.size / 1e6).toFixed(1)} MB) — using original.`
    );
    return file;
  }
  return out;

  // -- WASM path (DISABLED) --------------------------------------------------
  // Kept for reference only. Remove if no longer needed.
  // const t0 = performance.now();
  // try {
  //   const buffer = await file.arrayBuffer();
  //   const id = ++_msgId;
  //   const result = await new Promise((resolve, reject) => { ... });
  //   ...
  // } catch (err) { return file; }
};
// Export internal helpers for imageCompressionService to use
export { compressImageViaCompanion };
